<template>
  <TableMobileRow v-if="upgrade">
    <template #header>
      <div class="d-flex justify-content-between">
        <div>
          <LoadingState v-if="loading" />
          <div v-else class="ml-4 text-uppercase">{{ upgrade.name }}</div>
        </div>
        <div>
          <LoadingState v-if="loading" />
          <div v-else>{{ dateFormat(upgrade.completedDate, true) }}</div>
        </div>
        <div class="mr-4">
          <Pill
            v-if="upgrade.status === 'upgraded'"
            :content="$t('global.upgrade-successful')"
            :variant="getStatusVariant(upgrade.status)"
          />
          <Pill
            v-else-if="upgrade.status === 'failed'"
            :content="$t('global.did-not-upgrade')"
            :variant="getStatusVariant(upgrade.status + '-upgrade')"
          />
          <Pill v-else :content="upgrade.status" :variant="getStatusVariant(upgrade.status)" />
        </div>
      </div>
    </template>
    <template #content>
      <div class="mb-5" v-for="(regression, index) in regressions" :key="index">
        <div class="mb-4" v-if="regressions.length > 0">
          <LoadingState v-if="loading" />
          <div v-else class="d-flex">
            <div class="mr-4">
              {{ $t("global.path") }}:
              <span class="ml-2">
                {{ getPath(regression.appReferenceUrl) }}
              </span>
            </div>
            <div class="text-uppercase">
              <Pill
                :content="getDifferenceMessages(regression.status)"
                :variant="getStatusVariant(regression.status)"
              />
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-around visual-comparison-screenshots">
          <div>
            <div class="mb-2">{{ $t("global.before") }}: ({{ upgrade.version }})</div>
            <div>
              <img
                :src="regression.beforeImageUrl"
                @click="openImageModal(regression.beforeImageUrl)"
              />
            </div>
          </div>
          <div>
            <div class="mb-2">{{ $t("global.difference") }}: ({{ regression.score }}%)</div>
            <div>
              <img
                :src="regression.diffImageUrl"
                @click="openImageModal(regression.diffImageUrl)"
              />
            </div>
          </div>
          <div>
            <div class="mb-2">{{ $t("global.after") }}: ({{ upgrade.newVersion }})</div>
            <div>
              <img
                :src="regression.afterImageUrl"
                @click="openImageModal(regression.afterImageUrl)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </TableMobileRow>
</template>

<script>
import { onMounted, ref } from "@vue/composition-api";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import { getStatusVariant } from "@/utils/statusVariant";
import ImageViewerModal from "@/components/smart/modals/ImageViewer";
import LoadingState from "@/components/display/LoadingState";
import Pill from "@/components/display/Pill";
import store from "@/store";
import TableMobileRow from "@/components/display/table/TableMobileRow";

export default {
  name: "PluginUpgradeRow",
  props: {
    upgradeGroup: Object,
    site: Object,
  },
  components: {
    TableMobileRow,
    LoadingState,
    Pill,
  },
  setup(props, { root }) {
    const loading = ref(true);
    const upgrade = ref(null);
    const regressions = ref();

    const getPath = (fullUrl) => {
      const { pathname, search } = new URL(fullUrl);
      return `${pathname}${search}`;
    };

    const openImageModal = (url) =>
      store.dispatch("modals/open", {
        template: ImageViewerModal,
        data: { url },
      });

    const differenceMessages = {
      passed: root.$t("global.no-differences"),
      failed: root.$t("global.differences-detected"),
    };
    const getDifferenceMessages = (status) => differenceMessages[status] || status;

    onMounted(() => {
      store
        .dispatch("visualComparison/getUpgrade", {
          siteId: props.site.id,
          upgradeGroupId: props.upgradeGroup.id,
        })
        .then((data) => {
          upgrade.value = data;
          store
            .dispatch("visualComparison/getUpgradeRegressions", {
              siteId: props.site.id,
              upgradeGroupId: props.upgradeGroup.id,
              upgradeId: upgrade.value.id,
            })
            .then((data) => {
              regressions.value = data;
            })
            .finally(() => {
              loading.value = false;
            });
        });
    });

    return {
      loading,
      dateFormat,
      getStatusVariant,
      getDifferenceMessages,
      upgrade,
      regressions,
      getPath,
      openImageModal,
    };
  },
};
</script>

<style lang="scss" scoped>
.visual-comparison-screenshots img {
  width: 280px;
  height: auto;
  cursor: pointer;
}
</style>
